import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewChild } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { TranslateService } from '@ngx-translate/core'
import { Subject, takeUntil } from 'rxjs'
import { FullNameSegmentPipe } from '../../../pipes/fullname.segment.pipe'
import { ProgressCircleComponent } from '../../progress-circle/progress-circle.component'

@Component({
  selector: 'basic-input',
  templateUrl: './basic-input.component.html',
  styleUrls: ['./basic-input.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ProgressCircleComponent, FullNameSegmentPipe]
})
export class BasicInputComponent implements AfterViewInit {
  @Input() fieldType: BasicFieldType = BasicFieldType.TEXT
  @Input() control: FormControl = new FormControl()
  @Input() placeholder: string = ''
  @Input() label: string = ''
  @Input() icon: string = ''
  @Input() type: string = 'text'
  @Input() id?: string
  @Input() prefixSlug: string = ''
  @Input() pattern: string = ''
  @Input() image: string = ''
  @Input() iconType: string = 'fill'
  @Input() addValidationColors: boolean = true
  @Input() isLoading: boolean = false
  @Input() allowClearButton = true
  @Input() autocomplete: 'on' | 'off' | 'current-password' = 'on'
  @Input() required: boolean = false
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  @Input() autofocus: boolean = false
  /** When `true`, the native input is read-only (e.g. display-only triggers that open a picker). */
  @Input() readOnly: boolean = false
  @Input() isFullname: boolean = false
  @Input() fullnameParts: { fullnameSegments: string[]; processString: Record<string, string | null> } = {
    fullnameSegments: [],
    processString: {}
  }
  @Input() clearFullnameParts?: () => void
  @Input() min?: string
  @Input() max?: string

  // Emit value for parent component
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()
  @Output() onValueChange: EventEmitter<Event> = new EventEmitter<Event>()
  @Output() onFocusInput: EventEmitter<Event> = new EventEmitter<Event>()
  @Output() onWheel: EventEmitter<Event> = new EventEmitter<Event>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()

  @ViewChild('input') inputElement!: ElementRef
  @Output() emitInput: EventEmitter<ElementRef> = new EventEmitter<ElementRef>()

  public isFocused: boolean = false
  public basicFieldType = BasicFieldType
  public BasicInputMode = BasicInputMode

  private _destroy$: Subject<void> = new Subject<void>()
  private readonly _instanceId = crypto.randomUUID()

  get inputId(): string {
    return this.id ?? `floating_outlined_${this._instanceId}`
  }

  constructor(private _Sanitizer: DomSanitizer, private _Renderer: Renderer2, private _translate: TranslateService) {}

  ngAfterViewInit() {
    this.emitInput.emit(this.inputElement)
    if (this.inputElement) {
      this.calculateInputPadding()
      this.control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
        if (!value && this.fieldType === BasicFieldType.URL && this.isFocused) this.control.setValue(this.prefixSlug, { emitEvent: false })
        // Recalculate padding for all inputs including slug to handle dynamic changes
        this.calculateInputPadding()

        // Force clear fullname parts if the control becomes empty
        if (this.clearFullnameParts && (!value || value === '' || value.trim() === '')) this.clearFullnameParts()
      })
      if (this._hasPrefix()) this._setupPrefixedFieldEventListeners()

      // Handle autofocus
      if (this.autofocus) {
        setTimeout(() => {
          this.inputElement.nativeElement.focus()
          this.isFocused = true
        }, 0)
      }
    }
  }

  resetControl() {
    // Clear the input element directly first
    if (this.inputElement) this.inputElement.nativeElement.value = ''

    // Clear the form control
    this.control.setValue('', { emitEvent: true })
    this.control.markAsTouched()
    this.isFocused = false
  }
  /**
   * sanitize SVG icon to be rendered in template
   * @param icon string
   * @returns SafeHtml
   */
  public sanitizeSVG(icon: string): SafeHtml {
    return this._Sanitizer.bypassSecurityTrustHtml(icon)
  }

  /**
   * Emits control value when blur event is fired
   * @param event FocusEvent
   * @returns void
   */
  public onBlur(event: FocusEvent): void {
    // Always set isFocused to false when field loses focus
    this.isFocused = false
    this.onBlurValueChange.emit(event)
  }
  /**
   * Emits control value when change event is fired
   * @returns void
   */
  public onChange(event: Event): void {
    this.onValueChange.emit(event)
  }

  public onFocus(event: Event): void {
    this.isFocused = true
    if (this._hasPrefix()) this._handlePrefixedFieldFocus()

    this.onFocusInput.emit(event)
  }

  public onKeydownEvent(event: KeyboardEvent): void {
    if (this._hasPrefix()) if (this._handlePrefixedFieldKeydown(event)) return

    this.onKeydown.emit(event)
  }

  /**
   * @description Emit the wheel event
   * @param event accepts Event
   * @returns void
   */
  public onWheelEvent(event: Event): void {
    this.onWheel.emit(event)
  }

  public onPhoneInput(event: Event): void {
    if (this.fieldType === BasicFieldType.PHONE) {
      const input = event.target as HTMLInputElement
      // Remove all '+' from the value
      if (input.value.includes('+')) {
        input.value = input.value.replace(/\+/g, '')
        this.control.setValue(input.value)
      }
    }
    this.onChange(event)
  }

  /**
   * Calculates the padding of the input.
   * @returns The width of the input string in pixels.
   */
  calculateInputPadding() {
    const input = this.inputElement.nativeElement
    let width = ''

    if (this.fieldType === BasicFieldType.SLUG && this.prefixSlug) {
      // Create a temporary span to measure the actual width of the prefix text
      const tempSpan = document.createElement('span')
      tempSpan.style.visibility = 'hidden'
      tempSpan.style.position = 'absolute'
      tempSpan.style.whiteSpace = 'nowrap'
      tempSpan.style.fontSize = '14px'
      tempSpan.style.fontFamily = 'Inter, sans-serif'
      tempSpan.style.fontWeight = '500'
      tempSpan.textContent = this.prefixSlug

      document.body.appendChild(tempSpan)
      const prefixWidth = tempSpan.offsetWidth
      document.body.removeChild(tempSpan)

      // Add some spacing to prevent text overlap
      const totalPadding = prefixWidth + 29
      width = totalPadding + 'px'
      this._Renderer.setStyle(input, 'padding-left', width)
      return
    }

    width =
      this.icon && this.fieldType !== BasicFieldType.PHONE
        ? '36px'
        : this.icon && this.fieldType === BasicFieldType.PHONE
        ? '48px'
        : this.control.value && this.fieldType === BasicFieldType.PHONE
        ? '48px'
        : '12px'

    this._Renderer.setStyle(input, 'padding-left', width)
  }
  /**
   * Returns a prioritized error message from a FormControl.
   *
   * @returns {string} The highest priority validation error message
   * or an empty string if no errors exist.
   */
  public getErrorMessage(): string {
    if (this.control.errors) {
      for (const propertyName in this.control.errors) {
        if (this.control.errors.hasOwnProperty(propertyName) && (this.control.dirty || this.control.touched)) {
          switch (propertyName) {
            case 'required':
              return 'This field is required.'
            case 'invalidNumber':
              return this._translate.instant('v2.errors.invalidNumber')
            case 'wrongRange':
              return this.control.errors['wrongRange']
            case 'twoLetters':
              return this.control.errors['twoLetters']
            case 'twoNames':
              return this.control.errors['twoNames']
            case 'minlength':
              return this._translate.instant('v2.errors.minLength', { length: this.control.errors['minlength'].requiredLength })
            case 'maxlength':
              return this._translate.instant('v2.errors.maxLength', { length: this.control.errors['maxlength'].requiredLength })
            case 'emailFormat':
              return this._translate.instant('v2.errors.emailFormat')
            case 'maxLength':
              return this._translate.instant('v2.errors.maxLength', { length: this.control.errors['maxLength'].requiredLength })
            case 'minLength':
              return this._translate.instant('v2.errors.minLength', { length: this.control.errors['minLength'].requiredLength })
            case 'passwordsNotMatching':
              return this._translate.instant('v2.errors.passwordsNotMatching')
            case 'slugTaken':
              return this._translate.instant('v2.errors.slugTaken')
            case 'slugLength':
              return this._translate.instant('Your short name is too short', { length: this.control.errors['slugLength'].requiredLength })
            case 'domainInvalid':
              return this._translate.instant('v2.errors.domainInvalid')
            case 'freeDomainEmail':
              return this._translate.instant('v2.errors.freeDomainEmail')
            case 'uniqueEmail':
              return this._translate.instant('v2.errors.uniqueEmail')
            case 'incorrectPassword':
              return this._translate.instant('v2.errors.incorrectPassword')
            case 'personaNameInvalid':
              return this._translate.instant('v2.errors.personaNameInvalid')
            case 'matchedNames':
              return this._translate.instant('v2.errors.matchedNames')
            case 'shortNameInvalid':
              return this._translate.instant('v2.errors.shortNameInvalid')
            case 'missMatchedNames':
              return this._translate.instant('v2.errors.missMatchedNames')
            case 'incorrect_max_limit':
              return this._translate.instant('v2.errors.incorrect_max_limit')
            case 'incorrect_min_limit':
              return this._translate.instant('v2.errors.incorrect_min_limit')
            case 'exceeds_limit':
              return this._translate.instant('v2.errors.exceeds_limit')
            case 'selectLimitError':
              return this._translate.instant('v2.errors.selectLimitError')
            case 'exceeds_max':
              return this._translate.instant('v2.errors.exceeds_max')
            case 'duplicateDomain':
              return this._translate.instant('v2.errors.duplicateDomain')
            case 'invalidWebsiteUrl':
              return this._translate.instant('v2.errors.invalidWebsiteUrl')
            case 'minDate':
              return this._translate.instant('v2.errors.minDate', { date: this.control.errors['minDate'].date })
            case 'maxDate':
              return this._translate.instant('v2.errors.maxDate', { date: this.control.errors['maxDate'].date })
            case 'invalidDate':
              return this._translate.instant('v2.errors.invalidDate')
            case 'pattern':
              if (this.fieldType === BasicFieldType.PASSWORD) {
                return this._translate.instant('v2.errors.passwordInvalid')
              } else if (this.fieldType === BasicFieldType.EMAIL) {
                return this._translate.instant('v2.errors.emailFormat')
              } else {
                return this._translate.instant('v2.errors.invalidValue')
              }
            case 'serverError':
              return this.control.errors['serverError']
            case 'minSelections':
              return typeof this.control.errors['minSelections'] === 'string' && this.control.errors['minSelections']
                ? this.control.errors['minSelections']
                : this._translate.instant('v2.errors.invalidValue')
            case 'maxSelections':
              return typeof this.control.errors['maxSelections'] === 'string' && this.control.errors['maxSelections']
                ? this.control.errors['maxSelections']
                : this._translate.instant('v2.errors.invalidValue')
            default:
              return typeof this.control.errors[propertyName] === 'string' && this.control.errors[propertyName]
                ? this.control.errors[propertyName]
                : this._translate.instant('v2.errors.invalidValue')
          }
        }
      }
    }
    return ''
  }

  /**
   * Returns a unique ID for the input segments
   * @param baseId string
   * @returns string
   */
  public getUniqueId(baseId: string): string {
    return `${baseId}-${this._instanceId}`
  }

  /**
   * Measures text width using input field font properties
   * @param text string - The text to measure
   * @returns number - The width in pixels
   */
  private _measureTextWidth(text: string): number {
    const tempSpan = document.createElement('span')
    tempSpan.style.visibility = 'hidden'
    tempSpan.style.position = 'absolute'
    tempSpan.style.whiteSpace = 'nowrap'
    tempSpan.style.fontSize = '14px'
    tempSpan.style.fontFamily = 'Inter, sans-serif'
    tempSpan.style.fontWeight = '500'
    tempSpan.textContent = text

    document.body.appendChild(tempSpan)
    const width = tempSpan.offsetWidth
    document.body.removeChild(tempSpan)

    return width
  }

  /**
   * Gets the current input left padding value (extracted from calculateInputPadding logic)
   * @returns number - The padding in pixels
   */
  private _getInputLeftPadding(): number {
    // Same logic as calculateInputPadding but return numeric value
    return this.icon && this.fieldType !== BasicFieldType.PHONE
      ? 36
      : this.icon && this.fieldType === BasicFieldType.PHONE
      ? 48
      : this.control.value && this.fieldType === BasicFieldType.PHONE
      ? 48
      : 12
  }

  /**
   * Gets the width of a word for segment display
   * @param word string
   * @returns number
   */
  public getWordWidth(word: string): number {
    const element = document.getElementById(word)
    if (element) {
      const width = element.offsetWidth + 2
      return width > 415 ? 415 : width
    }
    return 100
  }

  /**
   * Calculates the exact position of a segment based on its text position in the input
   * @param segmentKey string - The key of the segment (e.g., 'salutations', 'first_name')
   * @returns number - The left position in pixels
   */
  public getSegmentPosition(segmentKey: string): number {
    const segmentValue = this.fullnameParts.processString[segmentKey]
    if (!segmentValue) return 0

    const inputValue = this.control.value

    // Find where this segment starts in the input text
    const segmentStartIndex = inputValue.indexOf(segmentValue)
    let startPosition = segmentStartIndex !== -1 ? segmentStartIndex : 0

    // Measure text width up to this position
    const textWidth = this._measureTextWidth(inputValue.substring(0, startPosition))

    // Add spacing between segments (2px per segment that comes before this one)
    const segmentOrder = ['salutations', 'first_name', 'middle_name', 'last_name']
    const currentIndex = segmentOrder.indexOf(segmentKey)
    let segmentSpacing = 0

    if (currentIndex > 0) {
      let segmentsBeforeCount = 0
      for (let i = 0; i < currentIndex; i++) {
        if (this.fullnameParts.processString[segmentOrder[i]]) segmentsBeforeCount++
      }
      segmentSpacing = segmentsBeforeCount * 2 // 2px per previous segment
    }

    return textWidth + this._getInputLeftPadding() + segmentSpacing
  }

  /**
   * Gets the width of a segment based on its actual text content in the input field
   * @param segmentKey string - The key of the segment
   * @returns number - The width in pixels
   */
  public getSegmentWidth(segmentKey: string): number {
    const segmentValue = this.fullnameParts.processString[segmentKey]
    return segmentValue ? this._measureTextWidth(segmentValue) : 0
  }

  /**
   * Extracts the path part from a full URL by removing the domain
   * @param url The full URL to extract from
   * @param prefix The prefix to match against
   * @returns The extracted path or null if no match
   */
  private _extractValueFromUrl(url: string, prefix: string): string | null {
    if (!url || !prefix) return null

    // Clean the URL and prefix for comparison
    const cleanUrl = url.toLowerCase().trim()
    const cleanPrefix = prefix.toLowerCase().replace(/\/$/, '')

    // Create a regex to match the domain and extract everything after it
    const domainRegex = new RegExp(`https?://(www\\.)?${cleanPrefix.replace(/\./g, '\\.')}(.*)`, 'i')
    const match = cleanUrl.match(domainRegex)

    if (match) {
      // Extract the path part (everything after the domain)
      const path = match[2] || ''
      // Remove leading slash and return the path
      return path.replace(/^\/+/, '')
    }

    // If it doesn't match the full URL pattern, check if it's already just a path
    if (!cleanUrl.includes('linkedin.com') && !cleanUrl.includes('http')) {
      return cleanUrl
    }

    return null
  }

  /**
   * Checks if the current field has a prefix
   */
  private _hasPrefix(): boolean {
    return this.fieldType === BasicFieldType.URL && !!this.prefixSlug
  }

  /**
   * Handles all keydown events for prefixed fields (Ctrl+ A, Backspace, Delete, ArrowLeft, Home, Tab, Escape)
   * @param {KeyboardEvent} event
   * @returns {boolean}
   */
  private _handlePrefixedFieldKeydown(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement
    const cursorStart = input.selectionStart || 0
    const cursorEnd = input.selectionEnd || 0
    const prefixLength = this.prefixSlug.length
    const totalLength = input.value.length

    // Allow Ctrl+A to select all
    if (event.ctrlKey && event.key === 'a') return false

    // Handle full selection deletion (Ctrl+A + Delete)
    if ((event.key === 'Backspace' || event.key === 'Delete') && cursorStart === 0 && cursorEnd === totalLength && totalLength > prefixLength) {
      input.value = this.prefixSlug
      this.control.setValue(this.prefixSlug, { emitEvent: true })
      input.setSelectionRange(prefixLength, prefixLength)
      event.preventDefault()
      return true
    }

    // Prevent deletion before prefix (but allow deletion of selected content after prefix)
    if ((event.key === 'Backspace' || event.key === 'Delete') && cursorStart <= prefixLength && cursorStart === cursorEnd) {
      event.preventDefault()
      return true
    }

    // Prevent navigation before prefix
    if ((event.key === 'ArrowLeft' || event.key === 'Home') && cursorStart <= prefixLength) {
      event.preventDefault()
      input.setSelectionRange(prefixLength, prefixLength)
      return true
    }

    // Handle typing before prefix (but allow deletion of selected content)
    if (cursorStart <= prefixLength && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape', 'Enter', 'Backspace', 'Delete']
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault()
        input.setSelectionRange(prefixLength, prefixLength)
        const char = event.key
        if (char.length === 1) {
          const currentValue = input.value
          const newValue = currentValue.slice(0, prefixLength) + char + currentValue.slice(prefixLength)
          input.value = newValue
          this.control.setValue(newValue, { emitEvent: false })
          input.setSelectionRange(prefixLength + 1, prefixLength + 1)
        }
        return true
      }
    }

    return false
  }

  /**
   * Handles focus behavior for prefixed fields
   * @returns void
   */
  private _handlePrefixedFieldFocus(): void {
    if (this.inputElement && this.inputElement.nativeElement) {
      const prefixLength = this.prefixSlug.length
      const currentValue = this.inputElement.nativeElement.value

      // For URL fields, set prefix as initial value if empty
      if (!currentValue) {
        this.control.setValue(this.prefixSlug, { emitEvent: false })
        this.inputElement.nativeElement.setSelectionRange(prefixLength, prefixLength)
      }
      // Position cursor at the end of existing value
      else this.inputElement.nativeElement.setSelectionRange(currentValue.length, currentValue.length)
    }
  }

  /**
   * Sets up event listeners for prefixed fields for paste events
   * @returns void
   */
  private _setupPrefixedFieldEventListeners(): void {
    const input = this.inputElement.nativeElement

    // Handle paste events for URL fields
    input.addEventListener('paste', (event: ClipboardEvent) => {
      event.preventDefault()
      const clipboardData = event.clipboardData?.getData('text') || ''
      const extractedValue = this._extractValueFromUrl(clipboardData, this.prefixSlug)

      if (extractedValue !== null) {
        const newValue = this.prefixSlug + extractedValue
        input.value = newValue
        this.control.setValue(newValue, { emitEvent: true })
        setTimeout(() => input.setSelectionRange(newValue.length, newValue.length), 0)
      }
    })
  }
}
